//#region src/web/telemetry-sanitizer.ts
const REDACTED_PARAMS_KEY = "redactedParams";
const REMOVED_PLACEHOLDER = "[removed]";
const MAX_URL_DEPTH = 8;
const MAX_NODES = 5e4;
const MAX_SEGMENTS = 5e3;
const MAX_STRING_LENGTH = 1e5;
const MAX_VALUE_RULE_LENGTH = 4096;
const SENSITIVE_NAMES = new Set([
	"acceptedinvitefrom",
	"auth",
	"caregiverid",
	"code",
	"declinedinvitefrom",
	"error_description",
	"invitedcaregiver",
	"patientid",
	"state",
	"user"
]);
const SENSITIVE_NAME_FRAGMENTS = [
	"address",
	"birth",
	"challenge",
	"context",
	"dob",
	"email",
	"firstname",
	"fullname",
	"lastname",
	"middlename",
	"password",
	"phone",
	"secret",
	"session",
	"token",
	"username",
	"zip"
];
const EMAIL_VALUE = /^[^\s@/?#&=]+@[^\s@/?#&=]+\.[^\s@/?#&=]+$/;
const PHONE_VALUE = /^\+?1?[\s().-]*(?:\d[\s().-]*){10}$/;
const PHONE_FORMATTING = /[+\s().-]/;
const JWT_VALUE = /^eyJ[\w-]+\.[\w-]+\.[\w-]*$/;
const ABSOLUTE_URL = /https?:\/\/[^\s"<>`]+/gi;
const STARTS_ABSOLUTE = /^https?:\/\//i;
const EMBEDS_ABSOLUTE = /https?:\/\//i;
const ENCODED_URL_MARKER = /[?#]|%3f|%23/i;
const PUNCTUATION_ONLY = /^[.,;'!]+$/;
const URL_MARKER = /[?#]/;
const HAS_URL_SIGNAL = /[?#]|https?:\/\/|%[0-9a-f]{2}/i;
const PERCENT_ESCAPE = /%[0-9a-f]{2}/i;
const NOT_WHOLLY_ENCODED = /[\s?#:/]/;
const WHITESPACE = /\s/;
const QUERY_CONTINUATION = /^&\S*=/;
const POSITION_SUFFIX = /:\d+:\d+$/;
const TRAILING_PUNCTUATION = ".,;'!";
const CLOSER_TO_OPENER = {
	")": "(",
	"]": "[",
	"}": "{"
};
/**
* Removes sensitive query and fragment parameters from every string an
* envelope carries, so patient identity and credentials that ride in page URLs
* never reach telemetry.
*
* Best effort, fail closed. A parameter is removed when its lowercased name is
* on an exact list, contains a sensitive fragment, or when its decoded value
* looks like an email, a formatted phone number, or a JWT. Everything else is
* kept, including surrounding text, paths, `:line:col` suffixes and
* non-sensitive parameters, which keep their original encoding. URL-valued
* parameters (such as `callback=`) are cleaned recursively up to a depth of 8;
* a deeper parameter is removed rather than the envelope.
*
* Coverage: `baseData`, `data` and `ext` are walked recursively with
* copy-on-write (caller-owned objects are never mutated; `URL` and other
* `toJSON()` objects are cleaned via their string form), and URL-bearing
* string values on `tags` are updated in place. Absolute `http(s)` URLs are
* found inside prose, stacks and dependency names; a whole string that is a
* relative reference containing `?` or `#` is cleaned too; a wholly
* percent-encoded URL is decoded once, cleaned and re-encoded.
*
* When something was removed, `data.redactedParams` lists the sorted, unique
* categories that fired. That key is reserved: a caller-supplied value is
* always discarded.
*
* Known limits: a sensitive value under a neutral name (`?ref=5555550100`) is
* kept, as are sensitive values copied into free text without a URL around
* them. Bounds on node count, string length and parameter count remove the
* excess rather than the envelope. Also accepted: objects whose `toJSON` or
* getters return different values per read, boxed `String` objects,
* URL-bearing property keys, encoded strings over 100k characters, values
* over 4,096 characters skipping the value rules, object graphs beyond the
* node budget or about 2,000 levels deep, IDs containing `?param=`, and
* labels percent-encoded many times.
*
* Throws on anything it cannot read (such as a throwing getter). Callers must
* treat an exception as "do not send": the SDK would otherwise send the
* original, unsanitized envelope.
*
* @param envelope The telemetry item; its `baseData`, `data`, `ext` and `tags`
* are updated in place when needed.
* @returns Whether anything was removed, and which categories fired.
*/
function sanitizeEnvelope(envelope) {
	const pass = newPass();
	const reserved = withoutReservedKey(envelope);
	const baseData = walk(reserved.baseData, pass);
	const data = walk(reserved.data, pass);
	const ext = walk(envelope.ext, pass);
	cleanTags(envelope.tags, pass);
	assignIfChanged(envelope, {
		baseData,
		data,
		ext
	});
	return reportOutcome(envelope, pass);
}
/**
* Whether a single query parameter would be removed by `sanitizeEnvelope`.
* Use it to vet a value before copying it into telemetry yourself (for
* example the `pathway` tag).
*
* @param name The parameter name, any case.
* @param value The decoded parameter value.
*/
function isSensitiveParam(name, value) {
	return categoriesOf(name, value).length > 0;
}
function newPass() {
	return {
		categories: /* @__PURE__ */ new Set(),
		segments: 0,
		nodes: 0,
		finished: /* @__PURE__ */ new WeakMap(),
		active: /* @__PURE__ */ new WeakSet()
	};
}
function reportOutcome(envelope, pass) {
	if (pass.categories.size === 0) return { kind: "unchanged" };
	const categories = [...pass.categories].sort();
	envelope.data = {
		...envelope.data,
		[REDACTED_PARAMS_KEY]: categories.join(",")
	};
	return {
		kind: "sanitized",
		categories
	};
}
function assignIfChanged(envelope, fields) {
	[
		"baseData",
		"data",
		"ext"
	].filter((key) => fields[key] !== envelope[key]).forEach((key) => {
		envelope[key] = fields[key];
	});
}
function withoutReservedKey(envelope) {
	const { baseData } = envelope;
	const properties = withoutKey(baseData?.properties);
	return {
		data: withoutKey(envelope.data),
		baseData: properties === baseData?.properties ? baseData : {
			...baseData,
			properties
		}
	};
}
function withoutKey(bag) {
	if (!bag || !Object.hasOwn(bag, REDACTED_PARAMS_KEY)) return bag;
	return Object.fromEntries(Object.entries(bag).filter(([key]) => key !== REDACTED_PARAMS_KEY));
}
function cleanTags(tags, pass) {
	if (!tags) return;
	Object.entries(tags).forEach(([key, value]) => {
		const cleaned = walk(value, pass);
		if (cleaned !== value && !Reflect.set(tags, key, cleaned)) throw new Error("Cannot rewrite a telemetry tag");
	});
}
function walk(value, pass) {
	return walkUnknown(value, pass);
}
function walkUnknown(value, pass) {
	if (typeof value === "string") return cleanString(value, pass, 0);
	if (typeof value !== "object" || value === null) return value;
	return walkObject(value, pass);
}
function walkObject(object, pass) {
	if (pass.finished.has(object)) return pass.finished.get(object);
	if (pass.active.has(object)) return removed(pass, "shape:circular");
	if (++pass.nodes > MAX_NODES) return removed(pass, "limit:budget");
	pass.active.add(object);
	const result = rebuild(object, pass);
	pass.active.delete(object);
	pass.finished.set(object, result);
	return result;
}
function rebuild(object, pass) {
	if (hasToJson(object)) {
		const json = object.toJSON();
		const walked = walkUnknown(json, pass);
		return walked === json ? object : walked;
	}
	return Array.isArray(object) ? rebuildArray(object, pass) : rebuildRecord(object, pass);
}
function rebuildArray(items, pass) {
	const walked = items.map((item) => walkUnknown(item, pass));
	return walked.every((item, index) => item === items[index]) ? items : walked;
}
function rebuildRecord(object, pass) {
	const entries = Object.entries(object);
	const walked = entries.map(([key, value]) => [key, walkUnknown(value, pass)]);
	return walked.every(([, value], index) => value === entries[index]?.[1]) ? object : Object.fromEntries(walked);
}
function hasToJson(object) {
	return "toJSON" in object && typeof object.toJSON === "function";
}
function removed(pass, category) {
	pass.categories.add(category);
	return REMOVED_PLACEHOLDER;
}
function cleanString(text, pass, depth) {
	if (text.length > MAX_STRING_LENGTH) return truncateAtUrlMarker(text, pass, URL_MARKER, "limit:budget");
	if (QUERY_CONTINUATION.test(text)) return cleanQuery(text, pass, depth);
	if (isWhollyEncoded(text)) return cleanEncoded(text, pass, depth);
	if (isRelativeReference(text)) return cleanUrl(text, pass, depth);
	return text.replace(ABSOLUTE_URL, (token) => cleanToken(token, pass, depth));
}
function truncateAtUrlMarker(text, pass, marker, category) {
	const start = text.search(marker);
	if (start < 0) return text;
	pass.categories.add(category);
	return text.slice(0, start);
}
function isWhollyEncoded(text) {
	return !NOT_WHOLLY_ENCODED.test(text) && PERCENT_ESCAPE.test(text);
}
function isRelativeReference(text) {
	return URL_MARKER.test(text) && !WHITESPACE.test(text) && !EMBEDS_ABSOLUTE.test(text);
}
function cleanEncoded(text, pass, depth) {
	const decoded = safeDecode(text);
	if (decoded === void 0) return truncateAtUrlMarker(text, pass, ENCODED_URL_MARKER, "limit:malformed");
	if (decoded === text || !HAS_URL_SIGNAL.test(decoded)) return text;
	if (depth >= MAX_URL_DEPTH) return removed(pass, "limit:depth");
	const cleaned = cleanString(decoded, pass, depth + 1);
	return cleaned === decoded ? text : encodeURIComponent(cleaned);
}
function cleanToken(token, pass, depth) {
	const { core, trailer } = splitTrailer(token);
	if (PUNCTUATION_ONLY.test(trailer)) {
		const whole = cleanUrl(token, pass, depth);
		if (whole !== token) return whole;
	}
	return cleanUrl(core, pass, depth) + trailer;
}
function cleanUrl(url, pass, depth) {
	const [beforeHash, hash] = splitAt(url, "#");
	const [path, query] = splitAt(beforeHash, "?");
	return path + cleanedPart("?", query, pass, depth) + cleanedPart("#", hash, pass, depth);
}
function cleanedPart(marker, part, pass, depth) {
	if (part === void 0) return "";
	const cleaned = cleanQuery(part, pass, depth);
	return cleaned === "" && part !== "" ? "" : marker + cleaned;
}
function cleanQuery(query, pass, depth) {
	return query.split("&").flatMap((segment) => cleanSegment(segment, pass, depth)).join("&");
}
function cleanSegment(segment, pass, depth) {
	if (++pass.segments > MAX_SEGMENTS) return removeSegment(pass, ["limit:budget"]);
	const [name, value] = decodeSegment(segment);
	const categories = [...categoriesOf(name, value), ...literalPlusCategories(segment, value)];
	if (categories.length > 0) return removeSegment(pass, categories);
	return HAS_URL_SIGNAL.test(value) ? cleanNested(segment, value, pass, depth) : [segment];
}
function cleanNested(segment, value, pass, depth) {
	if (depth >= MAX_URL_DEPTH) return removeSegment(pass, ["limit:depth"]);
	const cleaned = isUrlValue(value) ? cleanUrl(value, pass, depth + 1) : cleanString(value, pass, depth + 1);
	return [cleaned === value ? segment : `${rawNameOf(segment)}=${encodeURIComponent(cleaned)}`];
}
function isUrlValue(value) {
	const marker = value.search(URL_MARKER);
	return STARTS_ABSOLUTE.test(value) || marker >= 0 && !WHITESPACE.test(value.slice(0, marker));
}
function removeSegment(pass, categories) {
	categories.forEach((category) => pass.categories.add(category));
	return [];
}
function decodeSegment(segment) {
	const [entry] = [...new URLSearchParams(segment)];
	return entry ?? ["", ""];
}
function literalPlusCategories(segment, formDecoded) {
	const literal = safeDecode(splitAt(segment, "=")[1] ?? "");
	return literal === void 0 || literal === formDecoded ? [] : valueCategories(literal);
}
function rawNameOf(segment) {
	return splitAt(segment, "=")[0];
}
function splitTrailer(token) {
	const core = trimUnbalancedCloser(trimPunctuation(token)).replace(POSITION_SUFFIX, "");
	return {
		core,
		trailer: token.slice(core.length)
	};
}
function trimPunctuation(text) {
	let end = text.length;
	while (end > 0 && TRAILING_PUNCTUATION.includes(text.charAt(end - 1))) end -= 1;
	return text.slice(0, end);
}
function trimUnbalancedCloser(text) {
	const closer = text.charAt(text.length - 1);
	const opener = CLOSER_TO_OPENER[closer];
	if (opener === void 0 || count(text, closer) <= count(text, opener)) return text;
	return text.slice(0, -1);
}
function count(text, character) {
	return text.split(character).length - 1;
}
function splitAt(text, marker) {
	const index = text.indexOf(marker);
	return index < 0 ? [text, void 0] : [text.slice(0, index), text.slice(index + 1)];
}
function safeDecode(text) {
	try {
		return decodeURIComponent(text);
	} catch {
		return;
	}
}
function categoriesOf(name, value) {
	const lowerName = name.toLowerCase();
	return [
		...SENSITIVE_NAMES.has(lowerName) ? [`list:${lowerName}`] : [],
		...SENSITIVE_NAME_FRAGMENTS.filter((fragment) => lowerName.includes(fragment)).map((fragment) => `name:${fragment}`),
		...valueCategories(value)
	];
}
function valueCategories(value) {
	if (value.length > MAX_VALUE_RULE_LENGTH) return [];
	return [
		...EMAIL_VALUE.test(value) ? ["value:email"] : [],
		...isFormattedPhone(value) ? ["value:phone"] : [],
		...JWT_VALUE.test(value) ? ["value:jwt"] : []
	];
}
function isFormattedPhone(value) {
	return PHONE_VALUE.test(value) && PHONE_FORMATTING.test(value);
}

//#endregion
export { isSensitiveParam, sanitizeEnvelope };